import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

const getPalette = (story, index) => {
  const palettes = [
    { cover: '#314a43', foil: '#e6c98c', page: '#f5ecd8' },
    { cover: '#70474b', foil: '#f1c7a7', page: '#f6e8db' },
    { cover: '#3d526d', foil: '#c8d8e7', page: '#e7eef2' },
    { cover: '#675342', foil: '#e3c48e', page: '#f4ead5' },
    { cover: '#4f426b', foil: '#d8c6eb', page: '#eee6f5' },
    { cover: '#315b61', foil: '#b8dfd8', page: '#e1f0eb' },
  ];
  const seed = String(story?.id || story?.title || index);
  const hash = Array.from(seed).reduce((total, character) => total + character.charCodeAt(0), 0);
  return palettes[hash % palettes.length];
};

const wrapTitle = (context, title, maxWidth) => {
  const words = String(title || 'Untitled Story').split(/\s+/);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (line && context.measureText(next).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines.slice(0, 4);
};

const createCoverTexture = (story, palette) => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 760;
  const context = canvas.getContext('2d');
  context.fillStyle = palette.cover;
  context.fillRect(0, 0, canvas.width, canvas.height);
  const glow = context.createRadialGradient(250, 240, 20, 250, 240, 360);
  glow.addColorStop(0, 'rgba(255,255,255,0.12)');
  glow.addColorStop(1, 'rgba(0,0,0,0.18)');
  context.fillStyle = glow;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = palette.foil;
  context.globalAlpha = 0.78;
  context.lineWidth = 3;
  context.strokeRect(30, 30, canvas.width - 60, canvas.height - 60);
  context.globalAlpha = 1;
  context.fillStyle = palette.foil;
  context.textAlign = 'center';
  context.font = '800 20px Nunito, sans-serif';
  context.letterSpacing = '4px';
  context.fillText('MAKE IT MINDFUL', 256, 86);
  context.font = '34px Georgia, serif';
  context.fillText('✦', 256, 142);
  context.font = '400 52px Georgia, serif';
  const lines = wrapTitle(context, story?.title || 'Untitled Story', 390);
  const startY = 340 - ((lines.length - 1) * 28);
  lines.forEach((line, index) => context.fillText(line, 256, startY + index * 66));
  context.globalAlpha = 0.85;
  context.fillRect(215, 585, 82, 2);
  context.font = '800 15px Nunito, sans-serif';
  context.fillText(`STORYBOOK · ${String(story?.index || 1).padStart(2, '0')}`, 256, 670);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
};

const createReadableCoverTexture = (story, palette) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1536;
  const context = canvas.getContext('2d');
  const author = story?.author_name || story?.author || 'Make It Mindful';
  const draw = (image = null) => {
    context.fillStyle = palette.cover;
    context.fillRect(0, 0, canvas.width, canvas.height);
    if (image) {
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      context.fillStyle = 'rgba(15, 22, 20, 0.46)';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    const shade = context.createLinearGradient(0, 560, 0, canvas.height);
    shade.addColorStop(0, 'rgba(15, 22, 20, 0.05)');
    shade.addColorStop(1, 'rgba(15, 22, 20, 0.54)');
    context.fillStyle = shade;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = palette.foil;
    context.globalAlpha = 0.92;
    context.lineWidth = 6;
    context.strokeRect(54, 54, canvas.width - 108, canvas.height - 108);
    context.globalAlpha = 1;
    context.fillStyle = palette.foil;
    context.textAlign = 'center';
    context.font = '800 28px Nunito, sans-serif';
    context.fillText('MAKE IT MINDFUL', 512, 150);
    context.font = '52px Georgia, serif';
    context.fillText(String.fromCodePoint(0x2726), 512, 230);
    context.shadowColor = 'rgba(0, 0, 0, 0.66)';
    context.shadowBlur = 14;
    context.font = '700 76px Georgia, serif';
    const lines = wrapTitle(context, story?.title || 'Untitled Story', 820);
    const startY = 770 - ((lines.length - 1) * 48);
    lines.forEach((line, index) => context.fillText(line, 512, startY + index * 98));
    context.shadowBlur = 0;
    context.fillRect(430, 1260, 164, 4);
    context.font = '800 22px Nunito, sans-serif';
    context.fillText(`BY ${String(author).toUpperCase()}`, 512, 1370);
    context.font = '800 20px Nunito, sans-serif';
    context.fillText(`STORYBOOK - ${String(story?.index || 1).padStart(2, '0')}`, 512, 1460);
  };
  draw();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  if (story?.cover_image_url) {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => { draw(image); texture.needsUpdate = true; };
    image.src = story.cover_image_url;
  }
  return texture;
};

const addFrontFrame = (group, material) => {
  const rail = (width, height, x, y) => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.025), material);
    mesh.position.set(x, y, 0.31);
    group.add(mesh);
  };
  rail(1.28, 0.018, 0, 1.43);
  rail(1.28, 0.018, 0, -1.43);
  rail(0.018, 2.88, -0.62, 0);
  rail(0.018, 2.88, 0.62, 0);
};

const makeBook = (story, index) => {
  const palette = getPalette(story, index);
  const root = new THREE.Group();
  root.userData.storyId = story.id;
  root.userData.baseY = 0.15;
  root.userData.index = index;
  root.rotation.z = (index % 3 - 1) * 0.035;

  const coverMaterial = new THREE.MeshStandardMaterial({ color: palette.cover, roughness: 0.72, metalness: 0.06 });
  const pageMaterial = new THREE.MeshStandardMaterial({ color: palette.page, roughness: 0.92 });
  const foilMaterial = new THREE.MeshStandardMaterial({ color: palette.foil, roughness: 0.28, metalness: 0.7 });
  const spineMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color(palette.cover).multiplyScalar(0.72), roughness: 0.8 });

  const pages = new THREE.Mesh(new THREE.BoxGeometry(1.42, 2.94, 0.36), pageMaterial);
  pages.position.z = -0.02;
  pages.castShadow = true;
  root.add(pages);

  const front = new THREE.Mesh(new THREE.BoxGeometry(1.58, 3.16, 0.1), coverMaterial.clone());
  front.position.z = 0.24;
  front.castShadow = true;
  front.userData.bookRoot = root;
  root.add(front);

  const back = new THREE.Mesh(new THREE.BoxGeometry(1.58, 3.16, 0.1), coverMaterial);
  back.position.z = -0.27;
  back.castShadow = true;
  root.add(back);

  const spine = new THREE.Mesh(new THREE.BoxGeometry(0.16, 3.16, 0.52), spineMaterial);
  spine.position.x = -0.78;
  spine.castShadow = true;
  root.add(spine);
  addFrontFrame(root, foilMaterial);

  const title = new THREE.Mesh(
    new THREE.PlaneGeometry(1.38, 2.72),
    new THREE.MeshBasicMaterial({ map: createReadableCoverTexture({ ...story, index: index + 1 }, palette), transparent: false })
  );
  title.position.set(0.03, 0, 0.305);
  title.userData.bookRoot = root;
  root.add(title);

  return root;
};

function StoryShelf3D({ stories, selectedStory, onSelect, onReady, onError }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const selectedStoryIdRef = useRef(selectedStory?.id);
  const onSelectRef = useRef(onSelect);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  selectedStoryIdRef.current = selectedStory?.id;
  onSelectRef.current = onSelect;
  onReadyRef.current = onReady;
  onErrorRef.current = onError;

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    } catch (error) {
      onErrorRef.current?.(error);
      return undefined;
    }

    onReadyRef.current?.();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.92;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#24150f');
    const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
    camera.position.set(0, 1.2, Math.max(8.5, stories.length * 1.32));
    camera.lookAt(0, 0.5, 0);

    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    const environmentTarget = pmremGenerator.fromScene(new RoomEnvironment(), 0.04);
    scene.environment = environmentTarget.texture;
    scene.environmentIntensity = 0.64;
    pmremGenerator.dispose();

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.075;
    // The shelf is a fixed piece of furniture. Browsing happens through the
    // wheel and book selection; the whole room should not rotate or drift.
    controls.enableRotate = false;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.screenSpacePanning = true;
    controls.minDistance = 5.4;
    controls.maxDistance = Math.max(12, stories.length * 2.1);
    controls.minPolarAngle = Math.PI * 0.34;
    controls.maxPolarAngle = Math.PI * 0.64;
    controls.target.set(0, 0.42, 0);

    scene.add(new THREE.HemisphereLight('#ffe9bd', '#170b07', 2.2));
    const keyLight = new THREE.DirectionalLight('#fff1cf', 3.2);
    keyLight.position.set(-4, 7, 7);
    keyLight.castShadow = true;
    scene.add(keyLight);
    const rimLight = new THREE.PointLight('#c87046', 3, 12);
    rimLight.position.set(5, 2, 4);
    scene.add(rimLight);

    const sceneGroup = new THREE.Group();
    scene.add(sceneGroup);
    const width = Math.max(1.8, Math.min(2.15, 11 / Math.max(stories.length, 5)));
    const totalWidth = (stories.length - 1) * width;

    stories.forEach((story, index) => {
      const book = makeBook(story, index);
      book.position.x = index * width - totalWidth / 2;
      book.userData.baseX = book.position.x;
      sceneGroup.add(book);
    });

    const backPanel = new THREE.Mesh(
      new THREE.BoxGeometry(Math.max(totalWidth + 4, 12), 4.6, 0.22),
      new THREE.MeshStandardMaterial({ color: '#321b12', roughness: 0.9 })
    );
    backPanel.position.set(0, 1.8, -0.5);
    backPanel.receiveShadow = true;
    scene.add(backPanel);

    const shelf = new THREE.Mesh(
      new THREE.BoxGeometry(Math.max(totalWidth + 4.5, 12), 0.38, 1.6),
      new THREE.MeshStandardMaterial({ color: '#8a5837', roughness: 0.66 })
    );
    shelf.position.set(0, -1.48, 0);
    shelf.receiveShadow = true;
    shelf.castShadow = true;
    scene.add(shelf);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered = null;
    let targetCameraX = 0;
    let animationFrame;

    const resize = () => {
      const { clientWidth, clientHeight } = container;
      renderer.setSize(clientWidth, clientHeight, false);
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    const getBookAtPointer = (event) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const intersections = raycaster.intersectObjects(sceneGroup.children, true);
      const hit = intersections.find((intersection) => intersection.object.userData.bookRoot);
      return hit?.object.userData.bookRoot || null;
    };

    const handlePointerMove = (event) => {
      hovered = getBookAtPointer(event);
      canvas.style.cursor = hovered ? 'pointer' : 'default';
    };
    const handlePointerDown = (event) => {
      const book = getBookAtPointer(event);
      if (book) {
        const story = stories.find((item) => item.id === book.userData.storyId);
        if (story) onSelectRef.current(story);
      }
    };
    const handleWheel = (event) => {
      event.preventDefault();
      targetCameraX = THREE.MathUtils.clamp(targetCameraX + event.deltaY * 0.004, -Math.max(1, totalWidth * 0.13), Math.max(1, totalWidth * 0.13));
    };
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    const animate = () => {
      const selectedIndex = Math.max(0, stories.findIndex((story) => story.id === selectedStoryIdRef.current));
      const selectedX = selectedIndex * width - totalWidth / 2;
      targetCameraX = THREE.MathUtils.lerp(targetCameraX, selectedX * 0.15, 0.04);
      camera.position.x = THREE.MathUtils.lerp(camera.position.x, targetCameraX, 0.055);
      controls.target.x = THREE.MathUtils.lerp(controls.target.x, camera.position.x * 0.3, 0.08);
      controls.update();
      sceneGroup.children.forEach((book, index) => {
        const isSelected = index === selectedIndex;
        const isHovered = hovered === book;
        const lift = isSelected ? 0.55 : isHovered ? 0.28 : 0;
        book.position.y = THREE.MathUtils.lerp(book.position.y, lift + 0.15, 0.1);
        book.rotation.y = THREE.MathUtils.lerp(book.rotation.y, isSelected ? -0.12 : isHovered ? -0.05 : 0, 0.1);
        book.scale.setScalar(THREE.MathUtils.lerp(book.scale.x, isSelected ? 1.04 : 1, 0.1));
      });
      renderer.render(scene, camera);
      animationFrame = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      controls.dispose();
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('wheel', handleWheel);
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => {
            material.map?.dispose();
            material.dispose();
          });
        }
      });
      renderer.dispose();
      environmentTarget.texture.dispose();
    };
  }, [stories]);

  return (
    <div ref={containerRef} className="story-shelf-3d" aria-label="Interactive 3D story shelf">
      <canvas ref={canvasRef} aria-hidden="true" />
      <div className="story-shelf-3d__hint">Drag your attention across the shelf · select a volume to inspect</div>
    </div>
  );
}

export default StoryShelf3D;
